import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Users, CheckCircle2, ArrowRight, ArrowLeft, Bed, Grid3x3, UserPlus, Mail } from 'lucide-react';
import cimfoniLogo from '@/assets/cimfoni_logo_transparent.png';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';

interface OnboardingWizardProps {
  onComplete: () => void;
  onSkip: () => void;
}

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ onComplete, onSkip }) => {
  const queryClient = useQueryClient();
  const { organizationId } = useAuth();
  const [step, setStep] = useState(1);
  const [createdSiteId, setCreatedSiteId] = useState<string | null>(null);
  
  // Step 2: Facility Info
  const [siteName, setSiteName] = useState('');
  const [siteAddress, setSiteAddress] = useState('');
  const [sitePhone, setSitePhone] = useState('');
  const [siteCapacity, setSiteCapacity] = useState('50');
  
  // Step 3: Room/Bed Grid
  const [roomPrefix, setRoomPrefix] = useState('');
  const [roomStart, setRoomStart] = useState('101');
  const [roomEnd, setRoomEnd] = useState('120');
  const [bedsPerRoom, setBedsPerRoom] = useState('2');
  
  // Step 4: Staff Member
  const [staffName, setStaffName] = useState('');
  const [staffEmail, setStaffEmail] = useState('');
  
  const [staffRole, setStaffRole] = useState<'staff' | 'supervisor'>('staff');
  
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateSite = async () => {
    if (!siteName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a facility name",
        variant: "destructive"
      });
      return;
    }

    setIsCreating(true);
    try {
      let orgId = organizationId;
      if (!orgId) {
        const { data: userData } = await supabase.auth.getUser();
        if (userData?.user) {
          const { data: orgMember } = await supabase
            .from('organization_members')
            .select('organization_id')
            .eq('user_id', userData.user.id)
            .limit(1)
            .maybeSingle();
          orgId = orgMember?.organization_id ?? null;
        }
      }
      if (!orgId) {
        throw new Error('No organization is linked to your account. Ask an owner to add you before creating a facility.');
      }

      const { data, error } = await supabase
        .from('sites')
        .insert([{
          name: siteName,
          address: siteAddress || null,
          phone: sitePhone || null,
          capacity: parseInt(siteCapacity) || 50,
          is_active: true,
          organization_id: orgId,
        }])
        .select()
        .single();

      if (error) throw error;

      setCreatedSiteId(data.id);
      queryClient.invalidateQueries({ queryKey: ['sites'] });

      toast({
        title: "Success!",
        description: "Facility created successfully",
      });

      setStep(3);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create facility",
        variant: "destructive"
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateRooms = async () => {
    if (!createdSiteId) {
      setStep(4);
      return;
    }

    const start = parseInt(roomStart);
    const end = parseInt(roomEnd);
    const beds = parseInt(bedsPerRoom);

    if (isNaN(start) || isNaN(end) || start > end) {
      toast({
        title: "Error",
        description: "Please enter valid room numbers",
        variant: "destructive"
      });
      return;
    }

    setIsCreating(true);
    try {
      const rooms = [];
      for (let i = start; i <= end; i++) {
        const baseNumber = roomPrefix ? `${roomPrefix}${i}` : `${i}`;
        
        if (beds === 1) {
          rooms.push({
            room_number: baseNumber,
            capacity: 1,
            site_id: createdSiteId
          });
        } else {
          for (let bed = 1; bed <= beds; bed++) {
            const subLetter = String.fromCharCode(96 + bed); // a, b, c, etc.
            rooms.push({
              room_number: `${baseNumber}${subLetter}`,
              capacity: 1,
              site_id: createdSiteId
            });
          }
        }
      }

      const { error } = await supabase
        .from('rooms')
        .insert(rooms);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['rooms'] });

      toast({
        title: "Success!",
        description: `Created ${rooms.length} bed assignments`,
      });

      setStep(4);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create rooms",
        variant: "destructive"
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateStaff = async () => {
    if (!staffName.trim() || !staffEmail.trim()) {
      toast({
        title: "Error",
        description: "Please fill in all staff member fields",
        variant: "destructive"
      });
      return;
    }

    setIsCreating(true);
    try {
      const { data: userData, error: invokeError } = await supabase.functions.invoke('create-user', {
        body: {
          email: staffEmail,
          full_name: staffName,
          role: staffRole,
          site_ids: createdSiteId ? [createdSiteId] : []
        }
      });

      if (invokeError) throw invokeError;
      if (userData?.error) throw new Error(userData.error);

      toast({
        title: "Success!",
        description: `Staff member created. Temporary password: ${userData?.tempPassword ?? '(see admin)'}`,
      });

      setStep(5);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create staff member",
        variant: "destructive"
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-primary/10 p-6">
      <Card className="w-full max-w-2xl shadow-2xl">
        <CardHeader className="text-center pb-8">
          <div className="mx-auto mb-4 flex items-center justify-center">
            <img src={cimfoniLogo} alt="Cimfoni" className="h-24 w-auto object-contain" />
          </div>
          <CardTitle className="text-3xl font-bold">Welcome to Your Shelter Management System</CardTitle>
          <CardDescription className="text-base mt-2">
            Let's get you set up in just a few steps
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-2">
                <div className={`flex items-center justify-center h-10 w-10 rounded-full border-2 transition-all ${
                  step >= i 
                    ? 'bg-primary border-primary text-primary-foreground' 
                    : 'border-muted-foreground/30 text-muted-foreground'
                }`}>
                  {step > i ? <CheckCircle2 className="h-5 w-5" /> : i}
                </div>
                {i < 5 && (
                  <div className={`h-0.5 w-12 transition-all ${
                    step > i ? 'bg-primary' : 'bg-muted-foreground/30'
                  }`} />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Welcome */}
          {step === 1 && (
            <div className="space-y-6 text-center">
              <div className="space-y-2">
                <h3 className="text-2xl font-semibold">Getting Started</h3>
                <p className="text-muted-foreground">
                  This quick setup will help you create your first facility and understand the system
                </p>
              </div>

              <div className="grid gap-4 py-6">
                <div className="flex items-start gap-4 text-left p-4 rounded-lg bg-muted/50">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Create Your First Facility</h4>
                    <p className="text-sm text-muted-foreground">
                      Set up your shelter location with basic information
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 text-left p-4 rounded-lg bg-muted/50">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Bed className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Set Up Room & Bed Grid</h4>
                    <p className="text-sm text-muted-foreground">
                      Create your bed assignments and room structure
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 text-left p-4 rounded-lg bg-muted/50">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">​</h4>
                    <p className="text-sm text-muted-foreground">
                      Create accounts for your team to access the system
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-center pt-4">
                <Button variant="outline" onClick={onSkip}>
                  Skip for Now
                </Button>
                <Button onClick={() => setStep(2)} className="gap-2">
                  Get Started
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Create Facility */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <h3 className="text-2xl font-semibold">Create Your First Facility</h3>
                <p className="text-muted-foreground">
                  Enter your shelter's basic information
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="siteName">Facility Name *</Label>
                  <Input
                    id="siteName"
                    placeholder="e.g., Main Street Shelter"
                    value={siteName}
                    onChange={(e) => setSiteName(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="siteAddress">Address (Optional)</Label>
                  <Input
                    id="siteAddress"
                    placeholder="123 Main Street, City, State"
                    value={siteAddress}
                    onChange={(e) => setSiteAddress(e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sitePhone">Phone (Optional)</Label>
                    <Input
                      id="sitePhone"
                      placeholder="(555) 123-4567"
                      value={sitePhone}
                      onChange={(e) => setSitePhone(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="siteCapacity">Capacity</Label>
                    <Input
                      id="siteCapacity"
                      type="number"
                      placeholder="50"
                      value={siteCapacity}
                      onChange={(e) => setSiteCapacity(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(1)} className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <Button 
                  onClick={handleCreateSite} 
                  disabled={isCreating || !siteName.trim()}
                  className="gap-2"
                >
                  {isCreating ? 'Creating...' : 'Create Facility'}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Room/Bed Grid Setup */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                  <Grid3x3 className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-2xl font-semibold">Set Up Room & Bed Grid</h3>
                <p className="text-muted-foreground">
                  Create your room structure and bed assignments
                </p>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="roomPrefix">Room Prefix (Optional)</Label>
                    <Input
                      id="roomPrefix"
                      placeholder="e.g., R"
                      value={roomPrefix}
                      onChange={(e) => setRoomPrefix(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">Will create rooms like R101, R102</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bedsPerRoom">Beds per Room</Label>
                    <Input
                      id="bedsPerRoom"
                      type="number"
                      min="1"
                      max="4"
                      value={bedsPerRoom}
                      onChange={(e) => setBedsPerRoom(e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="roomStart">Starting Room Number</Label>
                    <Input
                      id="roomStart"
                      type="number"
                      placeholder="101"
                      value={roomStart}
                      onChange={(e) => setRoomStart(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="roomEnd">Ending Room Number</Label>
                    <Input
                      id="roomEnd"
                      type="number"
                      placeholder="120"
                      value={roomEnd}
                      onChange={(e) => setRoomEnd(e.target.value)}
                    />
                  </div>
                </div>

                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    <strong>Preview:</strong>{' '}
                    {(() => {
                      const start = parseInt(roomStart) || 0;
                      const end = parseInt(roomEnd) || 0;
                      const beds = parseInt(bedsPerRoom) || 1;
                      const roomCount = Math.max(0, end - start + 1);
                      const totalBeds = roomCount * beds;
                      const prefix = roomPrefix || '';
                      const exampleBase = `${prefix}${start}`;
                      const example = beds > 1 
                        ? `${exampleBase}a, ${exampleBase}b${beds > 2 ? ', ...' : ''}`
                        : exampleBase;
                      return `${totalBeds} beds across ${roomCount} rooms (e.g. ${example})`;
                    })()}
                  </p>
                </div>
              </div>

              <div className="flex gap-3 justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(2)} className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep(4)}>
                    Skip for Now
                  </Button>
                  <Button 
                    onClick={handleCreateRooms} 
                    disabled={isCreating}
                    className="gap-2"
                  >
                    {isCreating ? 'Creating...' : 'Create Rooms'}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Add Staff Member */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                  <UserPlus className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-2xl font-semibold">Add Your First Staff Member</h3>
                <p className="text-muted-foreground">
                  Create an account for a team member
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="staffName">Full Name *</Label>
                  <Input
                    id="staffName"
                    placeholder="e.g., John Smith"
                    value={staffName}
                    onChange={(e) => setStaffName(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="staffEmail">Email Address *</Label>
                  <Input
                    id="staffEmail"
                    type="email"
                    placeholder="john.smith@example.com"
                    value={staffEmail}
                    onChange={(e) => setStaffEmail(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="staffRole">Role</Label>
                  <Select value={staffRole} onValueChange={(value: 'staff' | 'supervisor') => setStaffRole(value)}>
                    <SelectTrigger id="staffRole">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">Staff Member</SelectItem>
                      <SelectItem value="supervisor">Supervisor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex gap-2">
                    <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-900 dark:text-blue-100">
                      <p className="font-semibold mb-1">Temporary Password</p>
                      <p>A secure temporary password will be generated automatically when this staff account is created.</p>
                      <p className="mt-1 text-xs text-blue-700 dark:text-blue-300">They&apos;ll be prompted to change it on first login</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(3)} className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep(5)}>
                    Skip for Now
                  </Button>
                  <Button 
                    onClick={handleCreateStaff} 
                    disabled={isCreating || !staffName.trim() || !staffEmail.trim()}
                    className="gap-2"
                  >
                    {isCreating ? 'Creating...' : 'Create Staff Member'}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Complete */}
          {step === 5 && (
            <div className="space-y-6 text-center py-6">
              <div className="mx-auto h-20 w-20 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              </div>

              <div className="space-y-2">
                <h3 className="text-2xl font-semibold">You're All Set!</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Your facility is ready to go. You can now start managing clients, tracking services, and running your daily operations.
                </p>
              </div>

              <div className="bg-muted/50 rounded-lg p-6 text-left space-y-3">
                <h4 className="font-semibold">What You've Set Up:</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Created facility: <strong>{siteName}</strong></span>
                  </li>
                  {createdSiteId && parseInt(roomEnd) >= parseInt(roomStart) && (
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Set up room and bed grid with {((parseInt(roomEnd) - parseInt(roomStart) + 1) * parseInt(bedsPerRoom))} beds</span>
                    </li>
                  )}
                  {staffName && (
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Added staff member: <strong>{staffName}</strong></span>
                    </li>
                  )}
                </ul>
              </div>

              <div className="bg-blue-50 dark:bg-blue-950 p-6 rounded-lg border border-blue-200 dark:border-blue-800 text-left space-y-3">
                <h4 className="font-semibold text-blue-900 dark:text-blue-100">Next Steps:</h4>
                <ul className="space-y-2 text-sm text-blue-900 dark:text-blue-100">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Start adding clients to your facility</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Review and customize alert notification settings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Explore the AI Assistant for help and insights</span>
                  </li>
                </ul>
              </div>

              <Button onClick={onComplete} size="lg" className="gap-2">
                Go to Dashboard
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Step 3: Complete */}
          {step === 3 && (
            <div className="space-y-6 text-center py-6">
              <div className="mx-auto h-20 w-20 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              </div>

              <div className="space-y-2">
                <h3 className="text-2xl font-semibold">You're All Set!</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Your facility has been created successfully. You can now start adding clients, managing rooms, and tracking services.
                </p>
              </div>

              <div className="bg-muted/50 rounded-lg p-6 text-left space-y-3">
                <h4 className="font-semibold">Next Steps:</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Set up rooms and bed assignments in Facilities section</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Add staff members through User Management</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Start adding clients to your facility</span>
                  </li>
                </ul>
              </div>

              <Button onClick={onComplete} size="lg" className="gap-2">
                Go to Dashboard
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingWizard;
